import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import css from './CustomNavbar.module.css';
import logoImg from '../../../../assets/TheModernBuilder-White-Logo.svg';
import { NamedLink, InlineTextButton } from '../../../../components';
import { Link } from 'react-router-dom';
import Icons from '../../../../components/Icons/Icons';
import { getUserType } from '../../../../util/dataExtractors';
import { UserType } from '../../../../util/enums';
import { apiBaseUrl } from '../../../../util/api';

const NAV_LINKS = [
  { label: 'LeadFlow', href: '/leadflow' },
  { label: 'Marketplace', href: '/home-designs' },
  { label: 'Resources', href: null, hasDropdown: true },
];

const RESOURCES_LINKS = [
  { label: 'Blogs', href: '/blogs' },
  { label: 'About Us', href: '/about' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Construction Budget Planner', href: '/cost-estimation' },
  { label: 'Cost Calculator', href: '/self-build-blogs/how-much-does-it-cost-to-self-build-a-house-in-2026' },
];

const MenuIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    {open ? (
      <path d="M6 6L18 18M6 18L18 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
    ) : (
      <>
        <path d="M4 6H20" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 12H20" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <path d="M4 18H20" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </>
    )}
  </svg>
);

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path d="M6 9L12 15L18 9" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CustomNavbar = ({ currentUser, isAuthenticated, onLogout, navLinks: customNavLinks }) => {
  const activeNavLinks = customNavLinks || NAV_LINKS;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const history = useHistory();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const displayName =
    currentUser?.attributes?.profile?.firstName ||
    currentUser?.attributes?.profile?.displayName ||
    'Account';

  const isSelfBuilder = getUserType(currentUser) === UserType.SELF_BUILDER;
  const isPropertyDeveloper = getUserType(currentUser) === UserType.PROPERTY_DEVELOPER;
  const isCustomer = isSelfBuilder || isPropertyDeveloper;
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !isCustomer) return;
    const sharetribeUserId = currentUser?.id?.uuid;
    if (!sharetribeUserId) return;

    const fetchUnread = () => {
      fetch(`${apiBaseUrl()}/api/leadflow/customer-threads?sharetribeUserId=${sharetribeUserId}`, {
        credentials: 'include',
      })
        .then(r => r.json())
        .then(data => {
          const total = (data.data || []).reduce((sum, t) => sum + (t.unreadCount || 0), 0);
          setUnreadCount(total);
        })
        .catch(() => {});
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isCustomer, currentUser?.id?.uuid]);

  const handleLogout = () => {
    onLogout(history.push);
  };

  return (
    <nav className={`${css.navbar} ${scrolled ? css.navbarScrolled : ''}`}>
      <div className={css.inner}>
        {/* Logo */}
        <Link to="/" className={css.logoWrap}>
          <img src={logoImg} alt="The Modern Builder" className={css.logoImg} />
        </Link>

        {/* Desktop nav links */}
        <div className={css.navLinks}>
          {activeNavLinks.map(link => {
            if (link.hasDropdown) {
              return (
                <div
                  key={link.label}
                  className={css.resourcesWrap}
                  onMouseEnter={() => setResourcesOpen(true)}
                  onMouseLeave={() => setResourcesOpen(false)}
                >
                  <span className={css.navLink} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {link.label} <ChevronDown />
                  </span>
                  {resourcesOpen && (
                    <div className={css.resourcesDropdown}>
                      <div className={css.resourcesDropdownInner}>
                        {RESOURCES_LINKS.map(r => (
                          <a key={r.label} href={r.href} className={css.resourcesItem}>
                            {r.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            if (link.isExternal) {
              return (
                <a key={link.label} href={link.href} className={css.navLink} target="_blank" rel="noopener noreferrer">
                  {link.label}
                </a>
              );
            }
            return (
              <a key={link.label} href={link.href} className={css.navLink}>
                {link.label}
              </a>
            );
          })}
        </div>

        {/* Auth CTA */}
        <div className={css.ctaWrap}>
          {isAuthenticated ? (
            <div
              className={css.profileMenuWrapper}
              onMouseEnter={() => setProfileMenuOpen(true)}
              onMouseLeave={() => setProfileMenuOpen(false)}
            >
              <button className={css.ctaBtn}>
                <Icons name="signupIcon" />
                <span>{displayName}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>

              {profileMenuOpen && (
                <div className={css.dropdownMenu}>
                  <div className={css.dropdownInner}>
                    {!isSelfBuilder && (
                      <NamedLink className={css.dropdownItem} name="ManageListingsPage">
                        My Listings
                      </NamedLink>
                    )}
                    <NamedLink className={css.dropdownItem} name="ProfileSettingsPage">
                      Profile Settings
                    </NamedLink>
                    {isCustomer && (
                      <NamedLink
                        className={css.dropdownItem}
                        name="InboxPage"
                        params={{ tab: 'messages' }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          Messages
                          {unreadCount > 0 && (
                            <span style={{ background: '#E8402A', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </span>
                      </NamedLink>
                    )}
                    <InlineTextButton
                      rootClassName={css.dropdownItemLogout}
                      onClick={handleLogout}
                    >
                      Log out
                    </InlineTextButton>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <NamedLink name="LoginPage" className={css.ctaBtn}>
              <Icons name="signupIcon" />
              <span>Log in</span>
            </NamedLink>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className={css.hamburger}
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label="Toggle menu"
        >
          <MenuIcon open={mobileOpen} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className={css.mobileMenu}>
          {activeNavLinks.map(link => {
            if (link.hasDropdown) {
              return (
                <React.Fragment key={link.label}>
                  <span className={css.mobileLinkLabel}>{link.label}</span>
                  {RESOURCES_LINKS.map(r => (
                    <a key={r.label} href={r.href} className={css.mobileLinkIndented}>
                      {r.label}
                    </a>
                  ))}
                </React.Fragment>
              );
            }
            return (
              <a key={link.label} href={link.href} className={css.mobileLink}>
                {link.label}
              </a>
            );
          })}
          {isAuthenticated ? (
            <>
              <NamedLink className={css.mobileLink} name="ProfileSettingsPage">
                Profile Settings
              </NamedLink>
              {isCustomer && (
                <NamedLink className={css.mobileLink} name="InboxPage" params={{ tab: 'messages' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Messages
                    {unreadCount > 0 && (
                      <span style={{ background: '#E8402A', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </span>
                </NamedLink>
              )}
              <InlineTextButton
                rootClassName={css.mobileCta}
                onClick={handleLogout}
              >
                Log out
              </InlineTextButton>
            </>
          ) : (
            <NamedLink name="LoginPage" className={css.mobileCta}>
              Log in
            </NamedLink>
          )}
        </div>
      )}
    </nav>
  );
};

export default CustomNavbar;
